import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getStorageItem, setStorageItem } from '../utils/storage';
import { updateThemePreference, getLandlordProfile } from '../api/landlordApi';
import { AuthContext } from './AuthContext';
import { error as logError } from '../utils/logger';

type ThemePreference = 'light' | 'dark' | 'system';
type EffectiveTheme = 'light' | 'dark';

interface ThemeContextValue {
  theme: EffectiveTheme;
  themePreference: ThemePreference;
  setTheme: (preference: ThemePreference) => Promise<void>;
  toggleTheme: () => void;
}

interface AuthUser {
  landlord_id?: string;
  config?: { ui?: { theme?: ThemePreference } };
  preferences?: { ui?: { theme?: ThemePreference } };
  ui_preferences?: { theme?: ThemePreference };
  theme_preference?: ThemePreference;
  [key: string]: unknown;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (landlordData: AuthUser, tokens: unknown) => void;
  logout: () => void;
  updateUser: (landlordData: AuthUser) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * Get the effective theme based on preference and system settings
 */
const getEffectiveTheme = (preference: ThemePreference): EffectiveTheme => {
  if (preference === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return preference || 'dark'; // Default to dark if not set
};

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  // Optionally consume AuthContext - returns null if AuthProvider is not in tree
  // This allows ThemeProvider to work independently for public pages
  const authContext = useContext(AuthContext) as AuthContextValue | null;
  const user = authContext?.user || null;
  const [themePreference, setThemePreference] = useState<ThemePreference>(() => {
    // Check localStorage first
    const savedPreference = getStorageItem('themePreference') as ThemePreference | null;
    if (savedPreference) {
      return savedPreference;
    }
    // Default to dark mode if no preference set
    return 'dark';
  });

  const [effectiveTheme, setEffectiveTheme] = useState<EffectiveTheme>(() =>
    getEffectiveTheme(themePreference)
  );

  // Sync theme preference from backend on login
  useEffect(() => {
    // config.ui (new) or preferences.ui / ui_preferences / theme_preference (legacy)
    const backendPreference = user?.config?.ui?.theme || user?.preferences?.ui?.theme || user?.ui_preferences?.theme || user?.theme_preference;
    if (backendPreference) {
      setThemePreference(backendPreference);
      setStorageItem('themePreference', backendPreference);
    }
  }, [user]);

  // Update effective theme when preference changes
  useEffect(() => {
    const newEffectiveTheme = getEffectiveTheme(themePreference);
    setEffectiveTheme(newEffectiveTheme);

    // Apply theme to document
    const root = document.documentElement;
    if (newEffectiveTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    // Store preference in localStorage
    setStorageItem('themePreference', themePreference);
  }, [themePreference]);

  // Listen for system theme changes when preference is 'system'
  useEffect(() => {
    if (themePreference !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      const newTheme: EffectiveTheme = mediaQuery.matches ? 'dark' : 'light';
      setEffectiveTheme(newTheme);

      // Apply theme to document
      const root = document.documentElement;
      if (newTheme === 'dark') {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, [themePreference]);

  /**
   * Set theme preference and sync with backend
   */
  const setTheme = useCallback(async (preference: ThemePreference) => {
    setThemePreference(preference);

    // Sync with backend if user is logged in and auth context is available
    if (user?.landlord_id && authContext) {
      try {
        await updateThemePreference(user.landlord_id, preference);
        // Refresh user data to get updated preferences from backend
        const updatedUser = await getLandlordProfile();
        authContext.updateUser(updatedUser);
      } catch (error) {
        logError('Failed to update theme preference on backend:', error);
        // Continue anyway - preference is stored locally
      }
    }
  }, [user, authContext]);

  const toggleTheme = useCallback(() => {
    // Toggle between light and dark (skip system)
    const newPreference: ThemePreference = effectiveTheme === 'light' ? 'dark' : 'light';
    setTheme(newPreference);
  }, [effectiveTheme, setTheme]);

  return (
    <ThemeContext.Provider value={{
      theme: effectiveTheme,
      themePreference,
      setTheme,
      toggleTheme,
    }}>
      {children}
    </ThemeContext.Provider>
  );
};
